import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ImageUpload } from '../../components/shared/ImageUpload';
import { toast } from 'sonner';
import { User, Save, Loader2, Eye, EyeOff } from 'lucide-react';

export default function AdminProfile() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState({ name: '', email: '', profile_image_url: '' });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    api.get('/admin/profile').then(r => setProfile(r.data)).catch(() => {});
  }, []);

  async function handleSaveProfile() {
    setSaving(true);
    try {
      await api.put('/admin/profile', { name: profile.name, email: profile.email, profile_image_url: profile.profile_image_url });
      toast.success('Profile updated!');
      if (refreshUser) refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword() {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSavingPassword(true);
    try {
      await api.put('/admin/profile', { current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-profile-page">
      <div>
        <h1 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A] flex items-center gap-2"><User size={24} /> My Profile</h1>
        <p className="text-sm text-[#52525B] mt-1">Update your personal information and password</p>
      </div>

      <Card className="p-6 border border-zinc-200 space-y-4">
        <h2 className="font-['Outfit'] font-medium text-lg">Personal Information</h2>
        <div className="flex items-center gap-6">
          <ImageUpload value={profile.profile_image_url} onChange={v => setProfile(p => ({ ...p, profile_image_url: v }))} folder="profiles" />
          <div className="flex-1 space-y-3">
            <div>
              <Label>Full Name</Label>
              <Input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} data-testid="profile-name-input" />
            </div>
            <div>
              <Label>Email Address</Label>
              <Input value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} data-testid="profile-email-input" />
            </div>
          </div>
        </div>
        <Button onClick={handleSaveProfile} disabled={saving} className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" data-testid="save-profile-btn">
          {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Save Profile
        </Button>
      </Card>

      <Card className="p-6 border border-zinc-200 space-y-4">
        <h2 className="font-['Outfit'] font-medium text-lg">Change Password</h2>
        <div className="space-y-3">
          <div>
            <Label>Current Password</Label>
            <div className="relative">
              <Input type={showPasswords ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} data-testid="current-password-input" />
              <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400">
                {showPasswords ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <Label>New Password</Label>
            <Input type={showPasswords ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} data-testid="new-password-input" />
          </div>
          <div>
            <Label>Confirm New Password</Label>
            <Input type={showPasswords ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} data-testid="confirm-password-input" />
          </div>
        </div>
        <Button onClick={handleChangePassword} disabled={savingPassword} variant="outline" data-testid="change-password-btn">
          {savingPassword ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} Change Password
        </Button>
      </Card>
    </div>
  );
}
